import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  Deal,
  DealStage,
  DealsState,
  DealsActiveView,
  DealsViewItem,
} from "../../pages/Crm/components/Deals/utils/types";
import { DEFAULT_PIPELINE_KEY } from "../../pages/Crm/components/Deals/utils/constants";

const initialState: DealsState = {
  stages: [],
  isInitialLoad: true,
  activeView: "grid",
  selectedViewId: "all_deals",
  selectedPipelineKey: DEFAULT_PIPELINE_KEY,
  showMetrics: true,
  dealsViews: [],
  newlyCreatedDealsViewId: null,
  viewRefetchTrigger: 0,
  collapsedStageKeys: [],
  autoCollapseSignature: "",
  stagesLoadedSignature: "",
};

const dealsSlice = createSlice({
  name: "deals",
  initialState,
  reducers: {
    setDealStages: (state, action: PayloadAction<DealStage[]>) => {
      state.stages = action.payload;
    },
    setDealsInitialLoad: (state, action: PayloadAction<boolean>) => {
      state.isInitialLoad = action.payload;
    },
    setDealsActiveView: (state, action: PayloadAction<DealsActiveView>) => {
      state.activeView = action.payload;
    },
    setDealsSelectedViewId: (state, action: PayloadAction<string>) => {
      state.selectedViewId = action.payload;
    },
    setDealsPipelineKey: (state, action: PayloadAction<string>) => {
      state.selectedPipelineKey = action.payload;
    },
    setDealsShowMetrics: (state, action: PayloadAction<boolean>) => {
      state.showMetrics = action.payload;
    },
    updateDealInStage: (
      state,
      action: PayloadAction<{ stageKey: string; deal: Deal }>
    ) => {
      const { stageKey, deal } = action.payload;
      const stage = state.stages.find((s) => s.stage_key === stageKey);
      if (stage) {
        const idx = stage.deals.findIndex((d) => d.contract_id === deal.contract_id);
        if (idx !== -1) {
          stage.deals[idx] = deal;
        }
      }
    },
    updateStageDeals: (
      state,
      action: PayloadAction<{
        stageKey: string;
        deals: Deal[];
        total: number;
      }>
    ) => {
      const { stageKey, deals, total } = action.payload;
      const stage = state.stages.find((s) => s.stage_key === stageKey);
      if (stage) {
        stage.deals = deals;
        stage.total = total;
      }
    },
    appendStageDealsList: (
      state,
      action: PayloadAction<{
        stageKey: string;
        deals: Deal[];
        total: number;
        hasMore: boolean;
      }>
    ) => {
      const { stageKey, deals, total, hasMore } = action.payload;
      const stage = state.stages.find((s) => s.stage_key === stageKey);
      if (stage) {
        stage.deals = [...stage.deals, ...deals];
        stage.total = total;
        stage.has_more = hasMore;
      }
    },
    setDealsViews: (state, action: PayloadAction<DealsViewItem[]>) => {
      state.dealsViews = action.payload;
    },
    addDealsView: (state, action: PayloadAction<DealsViewItem>) => {
      state.dealsViews.push(action.payload);
    },
    updateDealsViewInState: (
      state,
      action: PayloadAction<Partial<DealsViewItem> & { id: string }>
    ) => {
      const idx = state.dealsViews.findIndex((v) => v.id === action.payload.id);
      if (idx !== -1) {
        state.dealsViews[idx] = { ...state.dealsViews[idx], ...action.payload };
      }
      if (state.selectedViewId === action.payload.id) {
        state.viewRefetchTrigger += 1;
      }
    },
    removeDealsView: (state, action: PayloadAction<string>) => {
      state.dealsViews = state.dealsViews.filter((v) => v.id !== action.payload);
    },
    setNewlyCreatedDealsViewId: (state, action: PayloadAction<string | null>) => {
      state.newlyCreatedDealsViewId = action.payload;
    },
    setCollapsedStageKeys: (state, action: PayloadAction<string[]>) => {
      state.collapsedStageKeys = action.payload;
    },
    toggleCollapsedStageKey: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (!key) return;
      const idx = state.collapsedStageKeys.indexOf(key);
      if (idx === -1) {
        state.collapsedStageKeys.push(key);
      } else {
        state.collapsedStageKeys.splice(idx, 1);
      }
    },
    clearCollapsedStageKeys: (state) => {
      state.collapsedStageKeys = [];
    },
    setDealsAutoCollapseSignature: (state, action: PayloadAction<string>) => {
      state.autoCollapseSignature = action.payload;
    },
    setDealsStagesLoadedSignature: (state, action: PayloadAction<string>) => {
      state.stagesLoadedSignature = action.payload;
    },
  },
});

export const {
  setDealStages,
  setDealsInitialLoad,
  setDealsActiveView,
  setDealsSelectedViewId,
  setDealsPipelineKey,
  setDealsShowMetrics,
  updateDealInStage,
  updateStageDeals,
  appendStageDealsList,
  setDealsViews,
  addDealsView,
  updateDealsViewInState,
  removeDealsView,
  setNewlyCreatedDealsViewId,
  setCollapsedStageKeys,
  toggleCollapsedStageKey,
  clearCollapsedStageKeys,
  setDealsAutoCollapseSignature,
  setDealsStagesLoadedSignature,
} = dealsSlice.actions;

export default dealsSlice.reducer;
